import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import ScrollAnimation from '../components/ScrollAnimation';
import axios from 'axios';
import './css/info.css';

// Weather API Key - In production this should be in an environment variable
const WEATHER_API_KEY = 'YOUR_API_KEY'; // Replace with your OpenWeatherMap API key
const EDINBURGH_LAT = 55.9533;
const EDINBURGH_LON = -3.1883;

const Info = () => {
  // State for weather data
  const [currentWeather, setCurrentWeather] = useState(null);
  const [weatherForecast, setWeatherForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs for scroll animations
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  
  // Track active timeline item on hover for enhanced interactivity
  const [activeTimelineItem, setActiveTimelineItem] = useState(null);
  
  // Mouse position state for parallax effects
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Get scroll progress for animations
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  
  // Transform scroll progress for various effects
  const timelineProgress = useTransform(scrollYProgress, [0, 1], [0, 100]);
  
  // Mouse move handler for parallax effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX / window.innerWidth - 0.5,
        y: e.clientY / window.innerHeight - 0.5
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    enter: { 
      opacity: 1,
      transition: { 
        duration: 0.7, 
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.5 } 
    }
  };
  
  const sectionVariants = {
    initial: { opacity: 0, y: 30 },
    enter: { 
      opacity: 1,
      y: 0,
      transition: { 
        type: "spring", 
        damping: 15 
      }
    }
  };
  
  // Timeline animation variants with improved staggered animation
  const timelineContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };
  
  const timelineItemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        when: "beforeChildren"
      }
    }
  };
  
  const timelineLineVariants = {
    hidden: { scaleY: 0, originY: 0 },
    visible: { 
      scaleY: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.3
      }
    }
  };
  
  const timelineDotVariants = {
    hidden: { 
      scale: 0,
      opacity: 0 
    },
    visible: { 
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        delay: 0.2
      }
    }
  };
  
  const timelineContentVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.2
      }
    }
  };
  
  // Interactive hover variants for timeline items
  const timelineHoverVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.03, 
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2), 0 0 15px rgba(212, 175, 55, 0.2)",
      borderColor: "rgba(212, 175, 55, 0.5)",
      backgroundColor: "rgba(35, 35, 35, 0.5)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    }
  };
  
  // Year box hover animation
  const yearHoverVariants = {
    initial: { scale: 1, backgroundColor: "rgba(212, 175, 55, 0.2)" },
    hover: { 
      scale: 1.1, 
      backgroundColor: "rgba(212, 175, 55, 0.4)",
      boxShadow: "0 0 15px rgba(212, 175, 55, 0.3)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };
  
  // Fetch weather data
  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        // Mock data for development purposes
        // In a production app, you would use the actual API
        const mockCurrentWeather = {
          main: {
            temp: 12,
            feels_like: 10,
            humidity: 76,
            pressure: 1012
          },
          weather: [
            { main: 'Clouds', description: 'scattered clouds', icon: '03d' }
          ],
          wind: { speed: 4.12, deg: 280 },
          clouds: { all: 40 },
          sys: { sunrise: 1683856800, sunset: 1683911400 }
        };
        
        const mockForecast = {
          list: [
            {
              dt: 1683892800,
              main: { temp: 14, temp_min: 10, temp_max: 14 },
              weather: [{ icon: '04d', main: 'Clouds', description: 'broken clouds' }]
            },
            {
              dt: 1683979200,
              main: { temp: 15, temp_min: 9, temp_max: 15 },
              weather: [{ icon: '10d', main: 'Rain', description: 'light rain' }]
            },
            {
              dt: 1684065600,
              main: { temp: 13, temp_min: 10, temp_max: 14 },
              weather: [{ icon: '01d', main: 'Clear', description: 'clear sky' }]
            },
            {
              dt: 1684152000,
              main: { temp: 17, temp_min: 12, temp_max: 17 },
              weather: [{ icon: '02d', main: 'Clouds', description: 'few clouds' }]
            },
            {
              dt: 1684238400,
              main: { temp: 16, temp_min: 11, temp_max: 16 },
              weather: [{ icon: '03d', main: 'Clouds', description: 'scattered clouds' }]
            }
          ]
        };

        // To use the actual API, uncomment these:
        // const currentRes = await axios.get(
        //   `https://api.openweathermap.org/data/2.5/weather?lat=${EDINBURGH_LAT}&lon=${EDINBURGH_LON}&units=metric&appid=${WEATHER_API_KEY}`
        // );
        // const forecastRes = await axios.get(
        //   `https://api.openweathermap.org/data/2.5/forecast?lat=${EDINBURGH_LAT}&lon=${EDINBURGH_LON}&units=metric&appid=${WEATHER_API_KEY}`
        // );
        // setCurrentWeather(currentRes.data);
        // setWeatherForecast(forecastRes.data);

        // Using mock data for now
        setCurrentWeather(mockCurrentWeather);
        setWeatherForecast(mockForecast);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching weather data:", err);
        setError("Failed to load weather data. Please try again later.");
        setLoading(false);
      }
    };

    fetchWeatherData();
  }, []);

  // Format time from Unix timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Format date for forecast
  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
  };
  
  return (
    <motion.div 
      className="info-page"
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageVariants}
      ref={containerRef}
    >
      <motion.div 
        className="info-header"
        variants={sectionVariants}
        style={{
          x: mousePosition.x * 15,
          y: mousePosition.y * 15
        }}
      >
        <h1>Edinburgh City Information</h1>
        <p className="info-subtitle">Everything you need to know about Scotland's historic capital</p>
      </motion.div>
      
      {/* Historic Information Section */}
      <motion.section 
        className="info-section" 
        variants={sectionVariants}
      >
        <h2>Edinburgh's Rich History</h2>
        <p>Edinburgh, the capital city of Scotland, is steeped in history that dates back thousands of years. From ancient settlements to its status as a center of the Enlightenment and beyond, Edinburgh's past has shaped its unique character, architecture, and culture.</p>
        
        <div className="timeline-wrapper">
          <div className="timeline">
            {/* Timeline line is created by CSS ::after */}
            
            <div className="timeline-item-container left-container">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <span className="timeline-year">7th Century</span>
                <h3>Edinburgh Castle Rock</h3>
                <p>The earliest documented reference to a fortress on Castle Rock dates back to this period, though it's believed the site was occupied since the Bronze Age.</p>
                <span className="left-container-arrow"></span>
              </div>
            </div>
            
            <div className="timeline-item-container right-container">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <span className="timeline-year">12th Century</span>
                <h3>Royal Burgh</h3>
                <p>Edinburgh was established as a royal burgh by King David I, granting the town certain trading rights and privileges that helped it flourish.</p>
                <span className="right-container-arrow"></span>
              </div>
            </div>
            
            <div className="timeline-item-container left-container">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <span className="timeline-year">1437</span>
                <h3>Capital City</h3>
                <p>Edinburgh officially became the capital of Scotland, cementing its status as the nation's political and cultural center.</p>
                <span className="left-container-arrow"></span>
              </div>
            </div>
            
            <div className="timeline-item-container right-container">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <span className="timeline-year">18th Century</span>
                <h3>The Enlightenment</h3>
                <p>During the Scottish Enlightenment, Edinburgh became an intellectual center, known as "Athens of the North," with great thinkers such as David Hume and Adam Smith.</p>
                <span className="right-container-arrow"></span>
              </div>
            </div>
            
            <div className="timeline-item-container left-container">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <span className="timeline-year">1767</span>
                <h3>New Town Development</h3>
                <p>Construction began on Edinburgh's New Town, a masterpiece of urban planning that would become one of the finest examples of Georgian architecture in the world.</p>
                <span className="left-container-arrow"></span>
              </div>
            </div>
            
            <div className="timeline-item-container right-container">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <span className="timeline-year">1995</span>
                <h3>UNESCO Recognition</h3>
                <p>Edinburgh's Old and New Towns were together designated as a UNESCO World Heritage Site, recognizing their outstanding universal value to humanity.</p>
                <span className="right-container-arrow"></span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Weather Section */}
      <motion.section 
        className="info-section weather-section"
        variants={sectionVariants}
      >
        <h2>Edinburgh Weather</h2>
        
        <AnimatePresence>
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading weather data...</p>
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              <h3>Current Weather</h3>
              <motion.div 
                className="current-weather"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ 
                  scale: 1.02, 
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.3), 0 0 20px rgba(212, 175, 55, 0.15)" 
                }}
              >
                <motion.img 
                  className="weather-icon" 
                  src={`https://openweathermap.org/img/wn/${currentWeather?.weather[0].icon}@2x.png`} 
                  alt={currentWeather?.weather[0].description}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10 }}
                />
                <div className="weather-details">
                  <motion.div 
                    className="weather-temp"
                    whileHover={{ scale: 1.1, color: "#e2c044" }}
                    transition={{ type: "spring", stiffness: 300, damping: 10 }}
                  >
                    {Math.round(currentWeather?.main.temp)}°C
                  </motion.div>
                  <div className="weather-description">{currentWeather?.weather[0].description}</div>
                  <div>Feels like: {Math.round(currentWeather?.main.feels_like)}°C</div>
                </div>
              </motion.div>
              
              <motion.div 
                className="weather-info-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {[
                  { label: "Humidity", value: `${currentWeather?.main.humidity}%` },
                  { label: "Wind Speed", value: `${currentWeather?.wind.speed} m/s` },
                  { label: "Pressure", value: `${currentWeather?.main.pressure} hPa` },
                  { label: "Sunrise", value: formatTime(currentWeather?.sys.sunrise) },
                  { label: "Sunset", value: formatTime(currentWeather?.sys.sunset) },
                  { label: "Cloud Cover", value: `${currentWeather?.clouds.all}%` }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="weather-info-item"
                    whileHover={{ 
                      scale: 1.05, 
                      backgroundColor: "rgba(40, 40, 40, 0.7)",
                      borderColor: "rgba(212, 175, 55, 0.4)"
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    <div className="info-label">{item.label}</div>
                    <motion.div 
                      className="info-value"
                      whileHover={{ scale: 1.1, color: "#e2c044" }}
                    >
                      {item.value}
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
              
              <h3>5-Day Forecast</h3>
              <motion.div 
                className="weather-forecast"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {weatherForecast?.list.map((day, index) => (
                  <motion.div 
                    key={index} 
                    className="forecast-day"
                    whileHover={{ 
                      y: -8,
                      scale: 1.05,
                      backgroundColor: "rgba(40, 40, 40, 0.7)",
                      borderColor: "rgba(212, 175, 55, 0.4)",
                      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.3), 0 0 15px rgba(212, 175, 55, 0.15)"
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    <motion.div 
                      className="forecast-date"
                      whileHover={{ scale: 1.1, color: "#e2c044" }}
                    >
                      {formatDate(day.dt)}
                    </motion.div>
                    <motion.img 
                      className="forecast-icon" 
                      src={`https://openweathermap.org/img/wn/${day.weather[0].icon}@2x.png`} 
                      alt={day.weather[0].description}
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ type: "spring", stiffness: 300, damping: 10 }}
                    />
                    <div className="forecast-temp">
                      <motion.span 
                        className="forecast-high"
                        whileHover={{ scale: 1.1, color: "#f39c12" }}
                      >
                        {Math.round(day.main.temp_max)}°
                      </motion.span>
                      <motion.span 
                        className="forecast-low"
                        whileHover={{ scale: 1.1 }}
                      >
                        {Math.round(day.main.temp_min)}°
                      </motion.span>
                    </div>
                    <div className="forecast-description">{day.weather[0].description}</div>
                  </motion.div>
                ))}
              </motion.div>
              
              <p>
                <em>Note: Edinburgh's weather is famously changeable. Be prepared for "four seasons in one day" 
                and always pack a waterproof jacket, even in summer!</em>
              </p>
            </>
          )}
        </AnimatePresence>
      </motion.section>
      
      {/* Airport Information Section */}
      <motion.section 
        className="info-section"
        variants={sectionVariants}
      >
        <h2>Airport Information</h2>
        
        <div className="airport-info">
          <motion.div 
            className="airport-card"
            whileHover={{ 
              y: -10,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 20px rgba(212, 175, 55, 0.2)"
            }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <h3 className="airport-name">Edinburgh Airport (EDI)</h3>
            <p>
              Edinburgh Airport is Scotland's busiest airport, serving over 14 million passengers annually. 
              Located about 8 miles (12 km) west of the city center, it offers direct flights to over 150 destinations 
              worldwide.
            </p>
            
            <div className="airport-detail-grid">
              {[
                { label: "Distance from City", value: "8 miles (12 km)" },
                { label: "Travel Time to City", value: "25-30 minutes" },
                { label: "Main Airlines", value: "British Airways, easyJet, Ryanair, Jet2" },
                { label: "International Routes", value: "150+ destinations" }
              ].map((detail, index) => (
                <motion.div 
                  className="airport-detail" 
                  key={index}
                  whileHover={{ 
                    backgroundColor: "rgba(35, 35, 35, 0.5)",
                    borderRadius: "8px",
                    padding: "10px",
                    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.2)"
                  }}
                >
                  <div className="detail-label">{detail.label}</div>
                  <motion.div 
                    className="detail-value"
                    whileHover={{ color: "#d4af37" }}
                  >
                    {detail.value}
                  </motion.div>
                </motion.div>
              ))}
            </div>
            
            <h3>Airport Transportation Options</h3>
            <ul className="interactive-list">
              {[
                {
                  title: "Airport Tram",
                  desc: "Direct tram service to the city center (£6.00 single, £8.50 return). Journey time approximately 30 minutes."
                },
                {
                  title: "Airlink 100 Bus",
                  desc: "Express bus service to Waverley Bridge (£4.50 single, £7.50 return). Journey time approximately 25 minutes."
                },
                {
                  title: "Taxi",
                  desc: "Available at the airport taxi rank (£25-£30). Journey time approximately 25 minutes."
                },
                {
                  title: "Car Hire",
                  desc: "Several car rental companies have desks in the airport terminal."
                }
              ].map((item, index) => (
                <motion.li
                  key={index}
                  whileHover={{ 
                    x: 10, 
                    color: "#d4af37",
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                >
                  <strong>{item.title}</strong> - {item.desc}
                </motion.li>
              ))}
            </ul>
            
            <h3>Airport Facilities</h3>
            <ul className="interactive-list">
              {[
                "Free WiFi throughout the terminal",
                "Shops, restaurants, and cafes",
                "Currency exchange", 
                "Left luggage facility",
                "Assistance for passengers with reduced mobility"
              ].map((facility, index) => (
                <motion.li
                  key={index}
                  whileHover={{ 
                    x: 10, 
                    color: "#d4af37",
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                >
                  {facility}
                </motion.li>
              ))}
            </ul>
          </motion.div>
          
          <motion.div 
            className="airport-card"
            whileHover={{ 
              y: -10,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 20px rgba(212, 175, 55, 0.2)"
            }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <h3 className="airport-name">Getting to Edinburgh from Other UK Airports</h3>
            <p>
              If you're flying into another UK airport, Edinburgh is easily accessible by train or bus:
            </p>
            
            <ul className="interactive-list">
              {[
                {
                  title: "From London",
                  desc: "Direct trains from King's Cross station (4.5-5 hours) or flights from Heathrow, Gatwick, Luton, or Stansted (1.5 hours)."
                },
                {
                  title: "From Manchester",
                  desc: "Direct trains (3-3.5 hours) or flights (1 hour)."
                },
                {
                  title: "From Glasgow",
                  desc: "Direct trains (1 hour) from Glasgow Queen Street station or express bus services (1-1.5 hours)."
                },
                {
                  title: "From Belfast",
                  desc: "Direct flights (1 hour) or ferry to Cairnryan and then train/bus connections."
                }
              ].map((item, index) => (
                <motion.li
                  key={index}
                  whileHover={{ 
                    x: 10, 
                    color: "#d4af37",
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                >
                  <strong>{item.title}</strong> - {item.desc}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </motion.section>
      
      {/* Transport Info Section */}
      <motion.section 
        className="info-section"
        variants={sectionVariants}
      >
        <h2>Edinburgh Transport Information</h2>
        
        <p>
          Edinburgh is a compact city with excellent public transport links, making it easy to explore without a car. 
          The historic center is best discovered on foot, while buses and trams can take you further afield.
        </p>
        
        {[
          {
            icon: "🚋",
            type: "Edinburgh Trams",
            info: "A modern tram network connecting the airport with the city center and New Town. Trams run every 7-10 minutes during the day.",
            price: "Single journey: £1.80-£6.00 depending on zones | Day ticket: £4.50 (city) or £10.00 (including airport)",
            link: "https://edinburghtrams.com/" 
          },
          {
            icon: "🚌",
            type: "Lothian Buses",
            info: "Comprehensive bus network covering all areas of the city. The main operator offers frequent services with night buses also available.",
            price: "Single journey: £1.80 | Day ticket: £4.50 | Night bus: £3.00",
            link: "https://www.lothianbuses.com/"
          },
          {
            icon: "🚕",
            type: "Black Cabs & Taxis",
            info: "Edinburgh's traditional black cabs can be hailed on the street or found at taxi ranks. App-based services like Uber are also available.",
            price: "Starting fare: £2.80 | Typical city center journey: £7-£10",
            link: "https://www.uber.com/gb/en/ride/"
          },
          {
            icon: "🚲",
            type: "Cycling",
            info: "Edinburgh has an expanding network of cycle paths. Just Eat Cycles offers bike sharing across the city, with numerous docking stations.",
            price: "1 hour: £1.50 | Day pass: £3.00 | Annual membership: from £90",
            link: "https://edinburghcyclehire.com/"
          },
          {
            icon: "🚶",
            type: "Walking",
            info: "Edinburgh's compact city center is perfect for exploring on foot. Note that the city is built on hills, so comfortable footwear is essential! The historic Royal Mile runs from Edinburgh Castle to Holyrood Palace.",
            price: "",
            link: "https://www.edinburgh.org/things-to-do/activities/walking-trails/"
          }
        ].map((transport, index) => (
          <motion.a 
            key={index}
            href={transport.link}
            target="_blank"
            rel="noopener noreferrer"
            className="transport-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ 
              opacity: 1, 
              y: 0,
              transition: {
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
                delay: index * 0.1
              }
            }}
            viewport={{ once: true, margin: "-50px" }}
            whileHover={{ 
              y: -10,
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.3), 0 0 20px rgba(212, 175, 55, 0.2)"
            }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <motion.div 
              className="transport-icon"
              whileHover={{ 
                scale: 1.1, 
                backgroundColor: "rgba(212, 175, 55, 0.3)",
                boxShadow: "0 0 20px rgba(212, 175, 55, 0.3)"
              }}
            >
              {transport.icon}
            </motion.div>
            <div className="transport-details">
              <motion.div 
                className="transport-type"
                whileHover={{ color: "#e2c044" }}
              >
                {transport.type}
              </motion.div>
              <div className="transport-info">
                {transport.info}
              </div>
              {transport.price && (
                <div className="transport-price">
                  {transport.price}
                </div>
              )}
            </div>
          </motion.a>
        ))}
        
        <h3>Transport Tips</h3>
        <ul className="interactive-list">
          {[
            "Consider getting a Transport for Edinburgh (TfE) Smartcard for integrated tram and bus travel",
            "Download the Transport for Edinburgh App for real-time bus and tram information",
            "Exact change is needed on buses unless you have a contactless payment card or Smartcard",
            "The city center has restricted parking and numerous one-way streets, so driving is not recommended for tourists",
            "Many streets in the Old Town are steep and cobbled, which can be challenging for those with mobility issues"
          ].map((tip, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ 
                opacity: 1, 
                x: 0,
                transition: {
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1],
                  delay: 0.3 + index * 0.1
                }
              }}
              viewport={{ once: true, margin: "-50px" }}
              whileHover={{ 
                x: 10, 
                color: "#d4af37",
                transition: { type: "spring", stiffness: 300, damping: 20 }
              }}
            >
              {tip}
            </motion.li>
          ))}
        </ul>
      </motion.section>
    </motion.div>
  );
};

export default Info;
